"use client";

export const dynamic = "force-static";

import { useState, useEffect } from "react";
import Link from "next/link";
import { 
  Sparkles, 
  Phone, 
  Activity, 
  FileText, 
  CheckCircle, 
  MessageSquare, 
  Calendar, 
  Truck, 
  ShieldAlert, 
  Volume2, 
  Play, 
  ArrowRight, 
  Search, 
  ArrowLeft, 
  Cpu, 
  Clock, 
  UserCheck, 
  MapPin, 
  Mail, 
  FileCheck,
  Smartphone,
  Info,
  DollarSign,
  Users,
  Zap,
  ShieldCheck,
  AlertTriangle,
  Percent,
  BarChart3
} from "lucide-react";

/**
 * Sample margin math for the Order Fulfillment simulator.
 * Numbers mirror production billing (invoiceUtils) and payroll (NursePayroll.js).
 */
const INCENTIVE_DEMO = {
  drug: "Remicade",
  visitHours: 3.5,
  operational: { recommendation: "Level 2 Support (Urgent & Distance factors)" },
  capped: { approvedLevel: "Level 0 (Standard Base - Budget Compliant)" },
  pickupOddsBase: 35,
  pickupOddsOptimized: 62,
  flagged: false,
};

export default function AtuloIntelligencePage() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [passError, setPassError] = useState(false);

  useEffect(() => {
    if (typeof window !== "undefined") {
      const auth = localStorage.getItem("atulointelligence_auth");
      if (auth === "true") {
        setIsAuthenticated(true);
      }
    }
  }, []);

  const [activeTab, setActiveTab] = useState("nurse");
  
  // Nurse Simulator State
  const [nurseChat, setNurseChat] = useState([
    { role: "agent", text: "Hello! I'm Atulo Intelligence, your friendly voice assistant for home infusion therapy. How can I help you today with your patients' schedules or visits?" }
  ]);
  const [isNurseTyping, setIsNurseTyping] = useState(false);
  const [nurseStep, setNurseStep] = useState(0);

  // Patient Simulator State
  const [patientChat, setPatientChat] = useState([
    { role: "agent", text: "Hi! I'm Atulo Intelligence. I'm here to help you coordinate your care. I have access to your schedule and treatment details. What can I do for you today?" }
  ]);
  const [isPatientTyping, setIsPatientTyping] = useState(false);
  const [patientAlert, setPatientAlert] = useState(null);

  // Customer Support Simulator State
  const [searchQuery, setSearchQuery] = useState("");
  const [supportResult, setSupportResult] = useState(null);
  const [isSearchingSupport, setIsSearchingSupport] = useState(false);

  // Documentation Agent Simulator State
  const [extractionStage, setExtractionStage] = useState("idle"); // idle, uploading, analyzing, completed
  const [extractedData, setExtractedData] = useState(null);

  // Order Fulfillment Agent Simulator State
  const [incentiveStage, setIncentiveStage] = useState("idle"); // idle, deciding, decided

  // Auto scroll chat windows
  useEffect(() => {
    const nurseContainer = document.getElementById("nurse-chat-container");
    if (nurseContainer) nurseContainer.scrollTop = nurseContainer.scrollHeight;
  }, [nurseChat, isNurseTyping]);

  useEffect(() => {
    const patientContainer = document.getElementById("patient-chat-container");
    if (patientContainer) patientContainer.scrollTop = patientContainer.scrollHeight;
  }, [patientChat, isPatientTyping]);

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center p-6 text-white font-sans selection:bg-emerald-500 selection:text-black">
        <div className="w-full max-w-md bg-slate-900 border border-slate-800 rounded-2xl p-8 shadow-2xl relative overflow-hidden">
          {/* Subtle glow/gradient */}
          <div className="absolute -top-40 -right-40 h-80 w-80 rounded-full bg-emerald-500/10 blur-3xl pointer-events-none" />
          <div className="absolute -bottom-40 -left-40 h-80 w-80 rounded-full bg-blue-500/10 blur-3xl pointer-events-none" />

          <div className="flex flex-col items-center mb-8 relative">
            <div className="h-12 w-12 rounded-xl bg-emerald-500/15 flex items-center justify-center mb-4 border border-emerald-500/30">
              <Sparkles className="h-6 w-6 text-emerald-400" />
            </div>
            <h1 className="text-xl font-bold tracking-tight text-white text-center">Atulo Intelligence</h1>
            <p className="mt-2 text-sm text-slate-400 text-center">
              Please enter the portal password to access this resource.
            </p>
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (password === "mjisthegoat") {
                setIsAuthenticated(true);
                if (typeof window !== "undefined") {
                  localStorage.setItem("atulointelligence_auth", "true");
                }
              } else {
                setPassError(true);
                setPassword("");
              }
            }}
            className="space-y-4 relative"
          >
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">
                Password
              </label>
              <input
                type="password"
                placeholder="••••••••••••"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (passError) setPassError(false);
                }}
                className={`w-full px-4 py-3 bg-slate-950 border rounded-xl text-sm placeholder-slate-700 focus:outline-none focus:ring-1 transition-all ${
                  passError
                    ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                    : "border-slate-800 focus:ring-emerald-500 focus:border-emerald-500"
                }`}
                autoFocus
              />
              {passError && (
                <p className="mt-2 text-xs font-semibold text-red-405 flex items-center gap-1 text-red-450">
                  <AlertTriangle className="h-3.5 w-3.5 text-red-400" />
                  Incorrect password. Please try again.
                </p>
              )}
            </div>

            <button
              type="submit"
              className="w-full py-3 px-4 bg-emerald-500 text-slate-950 font-bold rounded-xl text-sm hover:bg-emerald-400 hover:scale-[1.01] active:scale-[0.99] transition-all cursor-pointer shadow-lg shadow-emerald-500/10 border-none"
            >
              Access Portal
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Nurse Voice Prompts Trigger
  const triggerNursePrompt = (promptText, responseText, nextStepNum) => {
    if (isNurseTyping) return;
    
    // Add User Prompt
    setNurseChat(prev => [...prev, { role: "user", text: promptText }]);
    setIsNurseTyping(true);

    setTimeout(() => {
      setIsNurseTyping(false);
      setNurseChat(prev => [...prev, { role: "agent", text: responseText }]);
      if (nextStepNum !== undefined) {
        setNurseStep(nextStepNum);
      }
    }, 1500);
  };

  // Patient Voice Prompts Trigger
  const triggerPatientPrompt = (promptText, responseText, alertData = null) => {
    if (isPatientTyping) return;

    setPatientChat(prev => [...prev, { role: "user", text: promptText }]);
    setIsPatientTyping(true);
    setPatientAlert(null);

    setTimeout(() => {
      setIsPatientTyping(false);
      setPatientChat(prev => [...prev, { role: "agent", text: responseText }]);
      if (alertData) {
        setPatientAlert(alertData);
      }
    }, 1500);
  };

  // Support Search Trigger
  const handleSupportSearch = (query) => {
    setSearchQuery(query);
    setIsSearchingSupport(true);
    setSupportResult(null);

    setTimeout(() => {
      setIsSearchingSupport(false);
      if (query.toLowerCase().includes("clock")) {
        setSupportResult({
          title: "Clocking In & Out of Visits",
          video: true,
          steps: [
            "Navigate to your Scheduled Visits roster on the Home screen.",
            "Tap on the card for the patient visit you are starting (must be today's date).",
            "Scroll down to the Tracking section and click the blue 'Clock In' button.",
            "Say 'Clock me out' or click 'Clock Out' when leaving the home."
          ],
          tip: "Atulo automatically cross-references your current GPS location to verify the visit took place at the patient's registered address."
        });
      } else if (query.toLowerCase().includes("schedule")) {
        setSupportResult({
          title: "Scheduling / Rescheduling Visits",
          video: true,
          steps: [
            "Open the Calendar module from the sidebar or bottom bar.",
            "Select the day you want to schedule or drag-and-drop the recommended visit card.",
            "Verify with the patient via Chat that the proposed time works.",
            "Click 'Confirm' to finalize. This sends an automatic SMS invite to the patient."
          ],
          tip: "Unscheduled visits have recommended target dates based on the physician's prescribed dosage intervals."
        });
      } else {
        setSupportResult({
          title: "General Navigation & App Guide",
          video: false,
          steps: [
            "Tap the 'App Guide' icon in the upper-right corner of the home feed.",
            "Browse guide categories: Sign-In, Referrals, Calendars, Invoicing, and Documents.",
            "Each section contains step-by-step screenshots and video playbacks."
          ],
          tip: "You can also ask the Atulo Voice Assistant anytime 'How do I do X?' to pop up these cards automatically."
        });
      }
    }, 1000);
  };

  // Document Extraction Simulation Trigger
  const startExtractionDemo = () => {
    if (extractionStage !== "idle") return;
    
    setExtractionStage("uploading");
    
    setTimeout(() => {
      setExtractionStage("analyzing");
      
      setTimeout(() => {
        setExtractionStage("completed");
        setExtractedData({
          patient: {
            name: "Tony Stark",
            dob: "1970-05-29",
            phone: "555-019-1970",
            address: "10880 Malibu Point, Malibu, CA 90265"
          },
          physician: {
            name: "Dr. Helen Cho, MD",
            practice: "Stark Industries Medical Division",
            address: "Stark Tower Medical Center, Floor 80, New York, NY 10001",
            phone: "555-019-3000",
            fax: "555-019-3001",
            npi: "1970052900",
            specialty: "Biomedical Engineering & Cardiology"
          },
          insurance: {
            carrier: "Stark Industries Health Plan",
            policy: "ARC-REACTOR-88"
          },
          clinical: {
            icd10: "T82.898A",
            allergies: "Palladium",
            therapy: "New Element Synthesized Infusion 100 mg",
            frequency: "ONCE every 6 months (180 days) for 1 year",
            instructions: "Premedications: Acetaminophen 650 mg PO, Jarvis telemetry monitoring enabled. Monitor chest plate temperature. Educate patient on arc reactor status."
          }
        });
      }, 2000);
    }, 1200);
  };

  // Incentive Decision Simulation Trigger
  const startIncentiveDemo = () => {
    if (incentiveStage !== "idle") return;

    setIncentiveStage("deciding");

    setTimeout(() => {
      setIncentiveStage("decided");
    }, 1800);
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 font-sans relative overflow-x-hidden selection:bg-teal-500 selection:text-white">
      {/* Decorative Blur Backdrops */}
      <div className="absolute top-[-20%] left-[-20%] w-[600px] h-[600px] rounded-full bg-teal-400/5 blur-[150px] pointer-events-none" />
      <div className="absolute top-[20%] right-[-10%] w-[500px] h-[500px] rounded-full bg-emerald-400/5 blur-[150px] pointer-events-none" />
      <div className="absolute bottom-[-15%] left-[20%] w-[600px] h-[600px] rounded-full bg-cyan-400/5 blur-[150px] pointer-events-none" />

      {/* Header */}
      <header className="sticky top-0 z-50 w-full bg-white/80 backdrop-blur-md border-b border-slate-200/60 px-4 sm:px-8 py-4 shadow-sm">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2 select-none">
            <span className="font-serif text-2xl font-black text-slate-900 leading-none tracking-tight">Atúlo</span>
            <div className="h-7 w-7 flex items-center justify-center text-[#38dcae]">
              <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <rect x="8" y="7" width="8" height="12" rx="4" />
                <line x1="6" y1="13" x2="18" y2="13" />
                <circle cx="12" cy="3" r="1.5" fill="currentColor" />
              </svg>
            </div>
            <span className="text-[#15b78e] font-semibold text-xs ml-2 px-2 py-0.5 rounded-md bg-[#38dcae]/10 border border-[#38dcae]/20">intelligence</span>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative max-w-7xl mx-auto px-4 sm:px-8 pt-16 pb-12 text-center">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-teal-500/10 border border-teal-500/20 text-[#15b78e] text-xs font-bold uppercase tracking-wider mb-6 animate-fade-in">
          <Cpu className="h-3.5 w-3.5" />
          Autonomous Healthcare Coordination
        </div>
        
        <h1 className="text-4xl sm:text-5xl lg:text-7xl font-serif font-black tracking-tight text-slate-900 mb-6 leading-tight max-w-4xl mx-auto">
          Powering Care with <br />
          <span className="bg-gradient-to-r from-[#15b78e] via-teal-500 to-cyan-500 bg-clip-text text-transparent">
            5 Specialized AI Agents
          </span>
        </h1>
        
        <p className="text-lg sm:text-xl text-slate-600 max-w-2xl mx-auto leading-relaxed mb-8">
          Atulo Intelligence automates intake, schedules appointments, and coordinates patients and nurses. 
          A fully unified agent system built for home infusion therapy.
        </p>
      </section>

      {/* Tabs & Main Playground */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 pb-24 relative z-10">
        {/* Navigation Tabs */}
        <div className="flex flex-wrap justify-center gap-2 sm:gap-3 mb-12 border-b border-slate-200/80 pb-6">
          <button
            onClick={() => setActiveTab("nurse")}
            className={`px-5 py-3 rounded-2xl border font-semibold text-sm transition-all duration-200 flex items-center gap-2 ${
              activeTab === "nurse"
                ? "bg-[#38dcae] text-white border-[#38dcae] shadow-lg shadow-teal-500/15 scale-105"
                : "bg-white border-slate-200 text-slate-500 hover:text-slate-800 hover:bg-slate-50"
            }`}
          >
            <Volume2 className="h-4 w-4" />
            1. Nurse Voice Agent
          </button>
          <button
            onClick={() => setActiveTab("patient")}
            className={`px-5 py-3 rounded-2xl border font-semibold text-sm transition-all duration-200 flex items-center gap-2 ${
              activeTab === "patient"
                ? "bg-[#38dcae] text-white border-[#38dcae] shadow-lg shadow-teal-500/15 scale-105"
                : "bg-white border-slate-200 text-slate-500 hover:text-slate-800 hover:bg-slate-50"
            }`}
          >
            <Activity className="h-4 w-4" />
            2. Patient Agent
          </button>
          <button
            onClick={() => setActiveTab("support")}
            className={`px-5 py-3 rounded-2xl border font-semibold text-sm transition-all duration-200 flex items-center gap-2 ${
              activeTab === "support"
                ? "bg-[#38dcae] text-white border-[#38dcae] shadow-lg shadow-teal-500/15 scale-105"
                : "bg-white border-slate-200 text-slate-500 hover:text-slate-800 hover:bg-slate-50"
            }`}
          >
            <Smartphone className="h-4 w-4" />
            3. Customer Support Agent
          </button>
          <button
            onClick={() => setActiveTab("documentation")}
            className={`px-5 py-3 rounded-2xl border font-semibold text-sm transition-all duration-200 flex items-center gap-2 ${
              activeTab === "documentation"
                ? "bg-[#38dcae] text-white border-[#38dcae] shadow-lg shadow-teal-500/15 scale-105"
                : "bg-white border-slate-200 text-slate-500 hover:text-slate-800 hover:bg-slate-50"
            }`}
          >
            <FileText className="h-4 w-4" />
            4. Documentation Agent
          </button>
          <button
            onClick={() => setActiveTab("orders")}
            className={`px-5 py-3 rounded-2xl border font-semibold text-sm transition-all duration-200 flex items-center gap-2 ${
              activeTab === "orders"
                ? "bg-[#38dcae] text-white border-[#38dcae] shadow-lg shadow-teal-500/15 scale-105"
                : "bg-white border-slate-200 text-slate-500 hover:text-slate-800 hover:bg-slate-50"
            }`}
          >
            <ShieldCheck className="h-4 w-4" />
            5. Order Fulfillment Agent
          </button>
        </div>

        {/* Playground Container - Glassmorphic Outer Card */}
        <div className="bg-white border border-slate-200/80 rounded-3xl p-6 sm:p-10 shadow-xl relative">
          
          {/* TAB 1: NURSE VOICE AGENT */}
          {activeTab === "nurse" && (
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start animate-fade-in">
              {/* Left explanation */}
              <div className="lg:col-span-5 space-y-6 text-left">
                <div className="h-12 w-12 rounded-2xl bg-teal-500/10 border border-teal-500/20 flex items-center justify-center">
                  <Volume2 className="h-6 w-6 text-teal-600" />
                </div>
                <h3 className="text-2xl sm:text-3xl font-serif font-black text-slate-900">Nurse Support Agent</h3>
                <p className="text-slate-600 leading-relaxed text-sm sm:text-base">
                  A voice-first &quot;Walkie-Talkie&quot; assistant embedded inside the Atulo Mobile App. 
                  It allows nurses to manage schedules, log mileage, and verify drug administration details completely hands-free while visiting patients.
                </p>
                
                <div className="space-y-4">
                  <h4 className="text-xs font-bold text-slate-400 uppercase tracking-widest">Key Capabilities</h4>
                  <ul className="space-y-3">
                    <li className="flex gap-3 items-start text-xs sm:text-sm text-slate-700">
                      <CheckCircle className="h-4 w-4 text-[#38dcae] shrink-0 mt-0.5" />
                      <span><strong>Hands-Free Scheduling:</strong> Nurses move/reschedule visits verbally without digging through calendars.</span>
                    </li>
                    <li className="flex gap-3 items-start text-xs sm:text-sm text-slate-700">
                      <CheckCircle className="h-4 w-4 text-[#38dcae] shrink-0 mt-0.5" />
                      <span><strong>Drive-Data Tracking:</strong> Logs drive mileage and time separately and updates payroll instantly.</span>
                    </li>
                    <li className="flex gap-3 items-start text-xs sm:text-sm text-slate-700">
                      <CheckCircle className="h-4 w-4 text-[#38dcae] shrink-0 mt-0.5" />
                      <span><strong>Visit Validation Check-In/Out:</strong> Verbal clock-in and check-out to verify visit duration.</span>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Right Walkie Talkie Simulator */}
              <div className="lg:col-span-7 bg-slate-950 rounded-2xl border border-slate-900 p-6 flex flex-col h-[520px] shadow-inner relative overflow-hidden">
                <div className="flex items-center justify-between border-b border-slate-900 pb-3 mb-4 shrink-0">
                  <div className="flex items-center gap-2">
                    <span className="relative flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-500"></span>
                    </span>
                    <span className="text-xs font-bold uppercase tracking-wider text-slate-400">Atulo Intelligence Walkie-Talkie</span>
                  </div>
                  <span className="text-xs text-blue-400 font-semibold px-2 py-0.5 bg-blue-950/40 rounded border border-blue-900/30">NURSE MODE</span>
                </div>

                {/* Chat window */}
                <div id="nurse-chat-container" className="flex-1 overflow-y-auto space-y-4 pr-1 scrollbar-thin scrollbar-thumb-slate-800">
                  {nurseChat.map((msg, idx) => (
                    <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${
                        msg.role === 'user' 
                          ? 'bg-blue-600 text-white rounded-br-none shadow-md' 
                          : 'bg-slate-900 text-slate-300 rounded-bl-none border border-slate-800'
                      }`}>
                        {msg.text}
                      </div>
                    </div>
                  ))}
                  {isNurseTyping && (
                    <div className="flex justify-start">
                      <div className="bg-slate-900 border border-slate-800 rounded-2xl rounded-bl-none px-4 py-3 text-sm text-slate-500 flex items-center gap-2">
                        <span className="h-1.5 w-1.5 bg-slate-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                        <span className="h-1.5 w-1.5 bg-slate-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                        <span className="h-1.5 w-1.5 bg-slate-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                      </div>
                    </div>
                  )}

                  {/* Simulated tool result visual card */}
                  {nurseStep === 1 && !isNurseTyping && (
                    <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-4 space-y-3 animate-fade-in">
                      <div className="flex justify-between items-center text-xs border-b border-slate-800 pb-2">
                        <span className="font-semibold text-slate-400">VISIT SCHEDULE FOR NEXT WEEK</span>
                        <span className="text-blue-400">3 Pending</span>
                      </div>
                      <div className="space-y-2">
                        <div className="flex justify-between items-center bg-slate-950 p-2.5 rounded-lg border border-slate-800/40">
                          <div>
                            <p className="text-xs font-semibold text-white">Patient: Tony Stark</p>
                            <p className="text-[10px] text-slate-400">Arc Reactor Therapy</p>
                          </div>
                          <span className="text-xs text-amber-500 px-2 py-0.5 bg-amber-950/40 border border-amber-900/30 rounded font-semibold">PATIENT PROPOSED</span>
                        </div>
                        <div className="flex justify-between items-center bg-slate-950 p-2.5 rounded-lg border border-slate-800/40">
                          <div>
                            <p className="text-xs font-semibold text-white">Patient: Pepper Potts</p>
                            <p className="text-[10px] text-slate-400">Executive Health Infusion</p>
                          </div>
                          <span className="text-xs text-blue-400 px-2 py-0.5 bg-blue-950/40 border border-blue-900/30 rounded font-semibold">RECOMMENDED</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Simulated rescheduled confirmation */}
                  {nurseStep === 2 && !isNurseTyping && (
                    <div className="bg-slate-900/80 border border-blue-900/30 rounded-xl p-4 space-y-3 animate-fade-in">
                      <div className="flex items-center gap-2 text-xs font-bold text-emerald-400 uppercase">
                        <CheckCircle className="h-4 w-4" />
                        Tool Invocation: reschedule_visit (Success)
                      </div>
                      <div className="bg-slate-950 p-3 rounded-lg border border-slate-800">
                        <div className="flex justify-between text-xs mb-2">
                          <span className="text-slate-400">Therapy Visit</span>
                          <span className="text-white font-semibold">Tony Stark · Arc Reactor</span>
                        </div>
                        <div className="flex justify-between text-xs mb-2">
                          <span className="text-slate-400">New Date</span>
                          <span className="text-white font-semibold">Friday (This Week)</span>
                        </div>
                        <div className="flex justify-between text-xs">
                          <span className="text-slate-400">New Time</span>
                          <span className="text-white font-semibold">3:00 PM</span>
                        </div>
                      </div>
                      <p className="text-[11px] text-slate-400">
                        An automatic request has been sent to Patient Tony Stark to confirm the new appointment time.
                      </p>
                    </div>
                  )}

                  {/* Simulated mileage logging confirmation */}
                  {nurseStep === 3 && !isNurseTyping && (
                    <div className="bg-slate-900/80 border border-indigo-900/30 rounded-xl p-4 space-y-3 animate-fade-in">
                      <div className="flex items-center gap-2 text-xs font-bold text-indigo-400 uppercase">
                        <CheckCircle className="h-4 w-4" />
                        Tool Invocation: log_drive_data (Success)
                      </div>
                      <div className="bg-slate-950 p-3 rounded-lg border border-slate-800">
                        <div className="flex justify-between text-xs mb-2">
                          <span className="text-slate-400">Activity logged</span>
                          <span className="text-white font-semibold">Mileage Only</span>
                        </div>
                        <div className="flex justify-between text-xs">
                          <span className="text-slate-400">Recorded Distance</span>
                          <span className="text-indigo-400 font-bold">23.0 Miles</span>
                        </div>
                      </div>
                      <p className="text-[11px] text-slate-400">
                        Drive distance synced with the payroll portal. Drive time remains unmodified.
                      </p>
                    </div>
                  )}
                </div>

                {/* Simulated click prompts */}
                <div className="mt-4 pt-4 border-t border-slate-900 shrink-0">
                  <p className="text-xs font-semibold text-slate-400 mb-2">Select a voice prompt to test the simulator:</p>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => triggerNursePrompt(
                        "Show me my schedule for next week.",
                        "Here is your upcoming schedule. You have three visits next week, including Tony Stark on Arc Reactor and Pepper Potts on Executive Health. Visual details are displayed below.",
                        1
                      )}
                      className="px-3 py-2 bg-slate-900 hover:bg-slate-800 border border-slate-800 text-xs rounded-xl text-slate-300 transition-colors"
                      disabled={isNurseTyping}
                    >
                      &quot;Show next week&apos;s schedule&quot;
                    </button>
                    <button
                      onClick={() => triggerNursePrompt(
                        "Move my appointment with Patient Tony Stark to Friday at 3 pm.",
                        "Sure, let me take care of that. I am rescheduling Tony Stark's Arc Reactor visit to Friday at 3:00 PM. I've sent a proposal to the patient to confirm.",
                        2
                      )}
                      className="px-3 py-2 bg-slate-900 hover:bg-slate-800 border border-slate-800 text-xs rounded-xl text-slate-300 transition-colors"
                      disabled={isNurseTyping}
                    >
                      &quot;Move Tony Stark visit to Friday at 3pm&quot;
                    </button>
                    <button
                      onClick={() => triggerNursePrompt(
                        "Log 23 miles.",
                        "Done. I've logged twenty-three miles against your current visit. That's updated on your travel dashboard.",
                        3
                      )}
                      className="px-3 py-2 bg-slate-900 hover:bg-slate-800 border border-slate-800 text-xs rounded-xl text-slate-300 transition-colors"
                      disabled={isNurseTyping}
                    >
                      &quot;Log 23 miles&quot;
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* TAB 2: PATIENT CARE AGENT */}
          {activeTab === "patient" && (
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start animate-fade-in">
              {/* Left explanation */}
              <div className="lg:col-span-5 space-y-6 text-left">
                <div className="h-12 w-12 rounded-2xl bg-teal-500/10 border border-teal-500/20 flex items-center justify-center">
                  <Activity className="h-6 w-6 text-teal-600" />
                </div>
                <h3 className="text-2xl sm:text-3xl font-serif font-black text-slate-900">Patient Care Agent</h3>
                <p className="text-slate-600 leading-relaxed text-sm sm:text-base">
                  A supportive companion for home infusion patients. It guides patients through their complex treatment schedules, lets them message their care team, and reports critical delivery or clinical updates directly.
                </p>
                
                <div className="space-y-4">
                  <h4 className="text-xs font-bold text-slate-400 uppercase tracking-widest">Key Capabilities</h4>
                  <ul className="space-y-3">
                    <li className="flex gap-3 items-start text-xs sm:text-sm text-slate-700">
                      <CheckCircle className="h-4 w-4 text-[#38dcae] shrink-0 mt-0.5" />
                      <span><strong>Drug & Supply Alerts:</strong> Patients can report delivery arrivals or trigger supply confirmation notifications.</span>
                    </li>
                    <li className="flex gap-3 items-start text-xs sm:text-sm text-slate-700">
                      <CheckCircle className="h-4 w-4 text-[#38dcae] shrink-0 mt-0.5" />
                      <span><strong>Care Scheduling Coordination:</strong> Proposes or accepts reschedule requests from their nurse (negotiation workflow).</span>
                    </li>
                    <li className="flex gap-3 items-start text-xs sm:text-sm text-slate-700">
                      <CheckCircle className="h-4 w-4 text-[#38dcae] shrink-0 mt-0.5" />
                      <span><strong>Urgent Safety Safeguards:</strong> Allows patients to directly indicate symptoms or allergic reactions to their nurse and pharmacy, showing immediate safety guidelines.</span>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Right Patient Simulator */}
              <div className="lg:col-span-7 bg-slate-950 rounded-2xl border border-slate-900 p-6 flex flex-col h-[520px] shadow-inner relative overflow-hidden">
                <div className="flex items-center justify-between border-b border-slate-900 pb-3 mb-4 shrink-0">
                  <div className="flex items-center gap-2">
                    <span className="relative flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-indigo-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-indigo-500"></span>
                    </span>
                    <span className="text-xs font-bold uppercase tracking-wider text-slate-400">Atulo Care Assistant</span>
                  </div>
                  <span className="text-xs text-indigo-400 font-semibold px-2 py-0.5 bg-indigo-950/40 rounded border border-indigo-900/30">PATIENT MODE</span>
                </div>

                {/* Chat window */}
                <div id="patient-chat-container" className="flex-1 overflow-y-auto space-y-4 pr-1 scrollbar-thin scrollbar-thumb-slate-800">
                  {patientChat.map((msg, idx) => (
                    <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${
                        msg.role === 'user' 
                          ? 'bg-indigo-600 text-white rounded-br-none shadow-md' 
                          : 'bg-slate-900 text-slate-300 rounded-bl-none border border-slate-800'
                      }`}>
                        {msg.text}
                      </div>
                    </div>
                  ))}
                  {isPatientTyping && (
                    <div className="flex justify-start">
                      <div className="bg-slate-900 border border-slate-800 rounded-2xl rounded-bl-none px-4 py-3 text-sm text-slate-500 flex items-center gap-2">
                        <span className="h-1.5 w-1.5 bg-slate-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                        <span className="h-1.5 w-1.5 bg-slate-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                        <span className="h-1.5 w-1.5 bg-slate-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                      </div>
                    </div>
                  )}

                  {/* Drug Arrival Tool Output Card */}
                  {patientAlert === "drug_arrival" && !isPatientTyping && (
                    <div className="bg-slate-900/80 border border-emerald-900/30 rounded-xl p-4 space-y-3 animate-fade-in">
                      <div className="flex items-center gap-2 text-xs font-bold text-emerald-400 uppercase">
                        <Truck className="h-4 w-4" />
                        Tool Invocation: report_drug_arrival (Triggered)
                      </div>
                      <div className="bg-slate-950 p-3 rounded-lg border border-slate-800">
                        <div className="text-xs space-y-1">
                          <p className="text-slate-400">Message sent to assigned nurse:</p>
                          <p className="text-white italic p-2 bg-slate-900 rounded border border-slate-800">
                            &quot;Hi Nurse, Patient reports that their medication/infusion supplies have arrived and are ready.&quot;
                          </p>
                        </div>
                      </div>
                      <p className="text-[11px] text-slate-400">
                        Successfully notified Nurse and Care Coordinator. Visit confirmation pending.
                      </p>
                    </div>
                  )}

                  {/* Allergic Reaction URGENT alert card */}
                  {patientAlert === "reaction" && !isPatientTyping && (
                    <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 space-y-3 animate-fade-in">
                      <div className="flex items-center gap-2 text-xs font-bold text-red-500 uppercase">
                        <ShieldAlert className="h-4 w-4" />
                        DIRECT NOTIFICATION: indicate_allergic_reaction
                      </div>
                      <div className="bg-slate-950 p-3 rounded-lg border border-red-500/20">
                        <p className="text-xs text-red-400 font-bold mb-1">CRITICAL INSTRUCTIONS SHOWN TO PATIENT:</p>
                        <p className="text-xs text-slate-300 leading-relaxed">
                          &quot;If you are experiencing difficulty breathing, swelling of the face/throat, or hives, please stop infusion immediately and CALL 911.&quot;
                        </p>
                      </div>
                      <div className="bg-slate-950 p-3 rounded-lg border border-slate-800 text-xs">
                        <p className="text-slate-400 font-medium">Automatic Alert broadcasted to:</p>
                        <ul className="list-disc list-inside mt-1 text-slate-300 space-y-1">
                          <li>Assigned Nurse (Push alert + SMS)</li>
                          <li>Stark Industries Pharmacy Dispatch Team (High Priority Ticket)</li>
                        </ul>
                      </div>
                    </div>
                  )}
                </div>

                {/* Simulated click prompts */}
                <div className="mt-4 pt-4 border-t border-slate-900 shrink-0">
                  <p className="text-xs font-semibold text-slate-400 mb-2">Select a patient query to test the simulator:</p>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => triggerPatientPrompt(
                        "My drugs have arrived.",
                        "Great! I've sent a notification to your nurse and pharmacy confirming your medication and infusion supplies are at your home.",
                        "drug_arrival"
                      )}
                      className="px-3 py-2 bg-slate-900 hover:bg-slate-800 border border-slate-800 text-xs rounded-xl text-slate-300 transition-colors"
                      disabled={isPatientTyping}
                    >
                      &quot;My drugs have arrived&quot;
                    </button>
                    <button
                      onClick={() => triggerPatientPrompt(
                        "I'm feeling hives and a rash on my arm during my infusion.",
                        "I have notified your nurse and pharmacy about your symptoms. If you have severe symptoms like difficulty breathing, please stop the infusion immediately and call 911. Otherwise, verify with your doctor or nurse.",
                        "reaction"
                      )}
                      className="px-3 py-2 bg-red-950/20 hover:bg-red-950/40 border border-red-900/30 text-xs rounded-xl text-red-200 transition-colors"
                      disabled={isPatientTyping}
                    >
                      &quot;Indicate allergic reaction&quot;
                    </button>
                    <button
                      onClick={() => triggerPatientPrompt(
                        "When is my next appointment?",
                        "Your next Arc Reactor infusion is scheduled with Nurse Samantha on Friday at 3:00 PM. I will notify you as soon as the nurse checks in."
                      )}
                      className="px-3 py-2 bg-slate-900 hover:bg-slate-800 border border-slate-800 text-xs rounded-xl text-slate-300 transition-colors"
                      disabled={isPatientTyping}
                    >
                      &quot;When is my next appointment?&quot;
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* TAB 3: CUSTOMER SUPPORT AGENT */}
          {activeTab === "support" && (
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start animate-fade-in">              {/* Left explanation */}
              <div className="lg:col-span-5 space-y-6 text-left">
                <div className="h-12 w-12 rounded-2xl bg-teal-500/10 border border-teal-500/20 flex items-center justify-center">
                  <Smartphone className="h-6 w-6 text-teal-600" />
                </div>
                <h3 className="text-2xl sm:text-3xl font-serif font-black text-slate-900">Customer Support Agent</h3>
                <p className="text-slate-600 leading-relaxed text-sm sm:text-base">
                  A smart in-app guide helper. If a nurse clicks on the &quot;Web Guide&quot; button or asks the voice assistant how to use a feature, the agent fetches specific documentation details and visual screenshots dynamically.
                </p>
                
                <p className="text-slate-500 text-xs">
                  It syncs directly with the central documentation catalog at: <br />
                  <a href="https://portalv2.atulocare.com/guide" target="_blank" rel="noreferrer" className="text-teal-600 underline hover:text-teal-500 font-semibold">
                    portalv2.atulocare.com/guide
                  </a>
                </p>
 
                <div className="space-y-4">
                  <h4 className="text-xs font-bold text-slate-400 uppercase tracking-widest">Key Capabilities</h4>
                  <ul className="space-y-3">
                    <li className="flex gap-3 items-start text-xs sm:text-sm text-slate-700">
                      <CheckCircle className="h-4 w-4 text-[#38dcae] shrink-0 mt-0.5" />
                      <span><strong>On-Demand Support:</strong> Guides nurses step-by-step through clock-ins, calendar, data entry, and reports.</span>
                    </li>
                    <li className="flex gap-3 items-start text-xs sm:text-sm text-slate-700">
                      <CheckCircle className="h-4 w-4 text-[#38dcae] shrink-0 mt-0.5" />
                      <span><strong>Dynamic Search Engine:</strong> Filters queries, strips English stopwords, stems keywords, and matches sections with strict relevance scores.</span>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Right Support Search Simulator */}
              <div className="lg:col-span-7 bg-slate-950 rounded-2xl border border-slate-900 p-6 flex flex-col h-[520px] shadow-inner relative overflow-hidden">
                <div className="flex items-center justify-between border-b border-slate-900 pb-3 mb-4 shrink-0">
                  <div className="flex items-center gap-2">
                    <Search className="h-4 w-4 text-teal-400" />
                    <span className="text-xs font-bold uppercase tracking-wider text-slate-400">Atulo Web Guide Search Engine</span>
                  </div>
                  <span className="text-xs text-teal-400 font-semibold px-2 py-0.5 bg-teal-950/40 rounded border border-teal-500/20">LIVE SEARCH</span>
                </div>

                {/* Search Input bar */}
                <div className="flex gap-2 mb-4 shrink-0">
                  <div className="relative flex-1">
                    <input
                      type="text"
                      placeholder="Ask how to do something (e.g. 'how do I clock in?')"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleSupportSearch(searchQuery)}
                      className="w-full bg-slate-900 border border-slate-800 rounded-xl py-2 pl-10 pr-3 text-xs sm:text-sm text-white focus:outline-none focus:border-teal-500"
                    />
                    <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-500" />
                  </div>
                  <button
                    onClick={() => handleSupportSearch(searchQuery)}
                    className="bg-teal-600 hover:bg-teal-500 text-white font-semibold text-xs px-4 rounded-xl transition-colors"
                  >
                    Search
                  </button>
                </div>

                {/* Search Results / guide presentation container */}
                <div className="flex-1 overflow-y-auto space-y-4 pr-1 scrollbar-thin scrollbar-thumb-slate-800">
                  {isSearchingSupport ? (
                    <div className="flex flex-col items-center justify-center h-full gap-2 text-slate-500">
                      <span className="relative flex h-5 w-5">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-teal-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-5 w-5 bg-teal-500"></span>
                      </span>
                      <p className="text-xs">Querying guide index...</p>
                    </div>
                  ) : supportResult ? (
                    <div className="bg-slate-900/40 border border-slate-800 rounded-xl p-4 sm:p-5 space-y-4 animate-fade-in">
                      <div className="flex justify-between items-start gap-2 border-b border-slate-800 pb-3">
                        <h4 className="font-extrabold text-sm sm:text-base text-white">{supportResult.title}</h4>
                        <span className="text-[10px] text-teal-400 font-semibold px-2 py-0.5 bg-teal-950/50 rounded border border-teal-500/20 uppercase shrink-0">
                          Verified Match
                        </span>
                      </div>

                      {/* Video Walkthrough Mock */}
                      {supportResult.video && (
                        <div className="rounded-lg overflow-hidden border border-slate-800 bg-slate-950 aspect-video flex items-center justify-center relative group">
                          <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-transparent to-transparent opacity-80" />
                          <div className="h-10 w-10 rounded-full bg-teal-600 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                            <Play className="h-5 w-5 text-white ml-0.5" />
                          </div>
                          <span className="absolute bottom-2 left-2 text-[10px] text-slate-400 font-semibold">Video Walkthrough (02:15)</span>
                        </div>
                      )}

                      {/* Steps */}
                      <div className="space-y-3">
                        <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Instructions:</p>
                        {supportResult.steps.map((step, idx) => (
                          <div key={idx} className="flex gap-3 items-start text-xs sm:text-sm">
                            <div className="shrink-0 w-5 h-5 rounded-full bg-teal-950 text-teal-400 font-bold text-xs flex items-center justify-center border border-teal-800/40">
                              {idx + 1}
                            </div>
                            <span className="text-slate-300">{step}</span>
                          </div>
                        ))}
                      </div>

                      {/* Tip Callout */}
                      <div className="p-3 bg-teal-950/20 border border-teal-900/30 rounded-xl flex gap-2.5 items-start">
                        <Info className="h-4 w-4 text-teal-400 shrink-0 mt-0.5" />
                        <p className="text-[11px] text-slate-400 leading-relaxed">
                          <span className="font-semibold text-slate-200">Tip:</span> {supportResult.tip}
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center h-full text-slate-500 p-6 text-center">
                      <HelpCircleIcon className="h-12 w-12 text-slate-700 mb-3" />
                      <h4 className="font-bold text-sm text-slate-400 mb-1">Interactive Help Desk</h4>
                      <p className="text-xs text-slate-500 max-w-sm">
                        Type a question above or click one of the quick suggestions below to search Atulo&apos;s mobile support documentation:
                      </p>
                      
                      <div className="flex flex-wrap gap-2 mt-4 justify-center">
                        <button
                          onClick={() => handleSupportSearch("how do I clock in?")}
                          className="px-3 py-1.5 bg-slate-900 hover:bg-slate-800 border border-slate-800 text-xs rounded-lg text-slate-400 transition-colors"
                        >
                          &quot;How do I clock in?&quot;
                        </button>
                        <button
                          onClick={() => handleSupportSearch("how do I reschedule?")}
                          className="px-3 py-1.5 bg-slate-900 hover:bg-slate-800 border border-slate-800 text-xs rounded-lg text-slate-400 transition-colors"
                        >
                          &quot;Scheduling visits&quot;
                        </button>
                        <button
                          onClick={() => handleSupportSearch("what is general guide?")}
                          className="px-3 py-1.5 bg-slate-900 hover:bg-slate-800 border border-slate-800 text-xs rounded-lg text-slate-400 transition-colors"
                        >
                          &quot;General Guide&quot;
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* TAB 4: DOCUMENTATION AGENT */}
          {activeTab === "documentation" && (
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start animate-fade-in">              {/* Left explanation */}
              <div className="lg:col-span-5 space-y-6 text-left">
                <div className="h-12 w-12 rounded-2xl bg-teal-500/10 border border-teal-500/20 flex items-center justify-center">
                  <FileText className="h-6 w-6 text-teal-600" />
                </div>
                <h3 className="text-2xl sm:text-3xl font-serif font-black text-slate-900">Documentation Agent</h3>
                <p className="text-slate-600 leading-relaxed text-sm sm:text-base">
                  Our Atulo Intelligence Extraction Agent. It reads incoming physician orders, face sheets, or emails, and automatically extracts structured patient details, doctor information, and clinical schedules.
                </p>
 
                <div className="space-y-4">
                  <h4 className="text-xs font-bold text-slate-400 uppercase tracking-widest">Key Capabilities</h4>
                  <ul className="space-y-3">
                    <li className="flex gap-3 items-start text-xs sm:text-sm text-slate-700">
                      <CheckCircle className="h-4 w-4 text-[#38dcae] shrink-0 mt-0.5" />
                      <span><strong>Instant Intake:</strong> Processes faxes or inbound email attachments and creates intake referrals automatically.</span>
                    </li>
                    <li className="flex gap-3 items-start text-xs sm:text-sm text-slate-700">
                      <CheckCircle className="h-4 w-4 text-[#38dcae] shrink-0 mt-0.5" />
                      <span><strong>High-Confidence Extraction:</strong> Parses Patient demographic details, NPI, ICD-10 codes, Allergies, and therapies.</span>
                    </li>
                    <li className="flex gap-3 items-start text-xs sm:text-sm text-slate-700">
                      <CheckCircle className="h-4 w-4 text-[#38dcae] shrink-0 mt-0.5" />
                      <span><strong>Schedule Generator:</strong> Extracts medication dosages and calculates the recurring schedule cycles automatically.</span>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Right Doc Extraction Simulator */}
              <div className="lg:col-span-7 bg-slate-950 rounded-2xl border border-slate-900 p-6 flex flex-col min-h-[520px] shadow-inner relative overflow-hidden">
                <div className="flex items-center justify-between border-b border-slate-900 pb-3 mb-4 shrink-0">
                  <div className="flex items-center gap-2">
                    <Cpu className="h-4 w-4 text-purple-400" />
                    <span className="text-xs font-bold uppercase tracking-wider text-slate-400">Atulo Intelligence Extraction Sandbox</span>
                  </div>
                  <span className="text-xs text-purple-400 font-semibold px-2 py-0.5 bg-purple-950/40 rounded border border-purple-500/20">AI RECOGNITION</span>
                </div>

                {extractionStage === "idle" && (
                  <div className="flex-1 flex flex-col items-center justify-center text-center p-6">
                    <div className="h-16 w-16 rounded-2xl bg-slate-900 border border-slate-800 flex items-center justify-center mb-4 text-slate-400">
                      <FileText className="h-8 w-8" />
                    </div>
                    <h4 className="font-bold text-sm text-slate-300 mb-1">Physician Referral Document Uploaded</h4>
                    <p className="text-xs text-slate-500 max-w-sm mb-6">
                      We have uploaded a sample physician referral file: <span className="text-purple-400 font-semibold">1984_1742169026270_Atulo-Health-Skilled-Nursing-Visit-Note.pdf</span>. Click the button to analyze and extract intake fields.
                    </p>
                    <button
                      onClick={startExtractionDemo}
                      className="bg-purple-600 hover:bg-purple-500 text-white font-bold text-xs sm:text-sm px-6 py-3 rounded-xl transition-all shadow-lg shadow-purple-500/10 active:scale-95"
                    >
                      Run AI Analysis
                    </button>
                  </div>
                )}

                {extractionStage === "uploading" && (
                  <div className="flex-1 flex flex-col items-center justify-center text-center gap-3">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500" />
                    <h4 className="font-bold text-xs text-slate-300 uppercase tracking-widest animate-pulse">Reading Document Pages...</h4>
                    <p className="text-xs text-slate-500">Downloading files and converting PDF pages to images...</p>
                  </div>
                )}

                {extractionStage === "analyzing" && (
                  <div className="flex-1 flex flex-col items-center justify-center text-center gap-3">
                    <div className="animate-pulse bg-purple-500/20 h-10 w-10 rounded-2xl flex items-center justify-center border border-purple-500/40">
                      <Cpu className="h-5 w-5 text-purple-400 animate-spin" />
                    </div>
                    <h4 className="font-bold text-xs text-slate-300 uppercase tracking-widest">Atulo Intelligence is analyzing your documents...</h4>
                    <p className="text-xs text-slate-500 max-w-xs leading-relaxed">
                      Calling Atulo Intelligence extraction pipeline to extract clinical information. This may take 30–60 seconds in production.
                    </p>
                  </div>
                )}

                {extractionStage === "completed" && extractedData && (
                  <div className="flex-1 overflow-y-auto space-y-4 pr-1 scrollbar-thin scrollbar-thumb-slate-800 animate-fade-in text-left">
                    <div className="flex justify-between items-center bg-purple-950/20 border border-purple-900/30 p-3 rounded-xl">
                      <div className="flex items-center gap-2">
                        <UserCheck className="h-4 w-4 text-purple-400" />
                        <span className="text-xs font-bold text-white uppercase tracking-wider">Extraction Success</span>
                      </div>
                      <button
                        onClick={() => {
                          setExtractionStage("idle");
                          setExtractedData(null);
                        }}
                        className="text-[10px] text-purple-400 hover:text-purple-300 font-semibold"
                      >
                        Reset Simulator
                      </button>
                    </div>

                    {/* Extracted Grid Layout matching physician orders portal */}
                    <div className="space-y-4">
                      
                      {/* Patient Block */}
                      <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-4 space-y-3">
                        <div className="flex gap-2 items-center text-xs font-bold text-slate-400 border-b border-slate-800 pb-1.5 uppercase">
                          <UserCheck className="h-3.5 w-3.5 text-blue-400" />
                          Patient Demographics
                        </div>
                        <div className="grid grid-cols-2 gap-4 text-xs">
                          <div>
                            <span className="text-slate-500 block mb-0.5">Name</span>
                            <span className="text-white font-semibold">{extractedData.patient.name}</span>
                          </div>
                          <div>
                            <span className="text-slate-500 block mb-0.5">Date of Birth</span>
                            <span className="text-white font-semibold">{extractedData.patient.dob}</span>
                          </div>
                          <div className="col-span-2">
                            <span className="text-slate-500 block mb-0.5">Address</span>
                            <span className="text-white font-semibold">{extractedData.patient.address}</span>
                          </div>
                        </div>
                      </div>

                      {/* Physician Block */}
                      <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-4 space-y-3">
                        <div className="flex gap-2 items-center text-xs font-bold text-slate-400 border-b border-slate-800 pb-1.5 uppercase">
                          <MapPin className="h-3.5 w-3.5 text-teal-400" />
                          Physician Information
                        </div>
                        <div className="grid grid-cols-2 gap-3 text-xs">
                          <div>
                            <span className="text-slate-500 block mb-0.5">Physician Name</span>
                            <span className="text-white font-semibold">{extractedData.physician.name}</span>
                          </div>
                          <div>
                            <span className="text-slate-500 block mb-0.5">Practice</span>
                            <span className="text-white font-semibold">{extractedData.physician.practice}</span>
                          </div>
                          <div>
                            <span className="text-slate-500 block mb-0.5">Phone</span>
                            <span className="text-white">{extractedData.physician.phone}</span>
                          </div>
                          <div>
                            <span className="text-slate-500 block mb-0.5">NPI Number</span>
                            <span className="text-white">{extractedData.physician.npi}</span>
                          </div>
                        </div>
                      </div>

                      {/* Clinical Information */}
                      <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-4 space-y-3">
                        <div className="flex gap-2 items-center text-xs font-bold text-slate-400 border-b border-slate-800 pb-1.5 uppercase">
                          <FileCheck className="h-3.5 w-3.5 text-purple-400" />
                          Clinical & Therapy Order Details
                        </div>
                        <div className="space-y-3 text-xs">
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <span className="text-slate-500 block mb-0.5">ICD-10 Code</span>
                              <span className="text-white font-semibold">{extractedData.clinical.icd10}</span>
                            </div>
                            <div>
                              <span className="text-slate-500 block mb-0.5">Allergies</span>
                              <span className="text-red-400 font-semibold">{extractedData.clinical.allergies}</span>
                            </div>
                          </div>
                          <div>
                            <span className="text-slate-500 block mb-0.5">Therapy Prescribed</span>
                            <span className="text-white font-semibold bg-slate-950 p-2 rounded block border border-slate-800">
                              {extractedData.clinical.therapy}
                            </span>
                          </div>
                          <div>
                            <span className="text-slate-500 block mb-0.5">Frequency & Duration</span>
                            <span className="text-white font-semibold">{extractedData.clinical.frequency}</span>
                          </div>
                          <div>
                            <span className="text-slate-500 block mb-0.5">Premedications & Instructions</span>
                            <p className="text-slate-300 leading-relaxed bg-slate-950 p-2 rounded border border-slate-800 text-[11px]">
                              {extractedData.clinical.instructions}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* TAB 5: ORDER FULFILLMENT AGENT */}
          {activeTab === "orders" && (
            <div className="animate-fade-in">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
                {/* Left explanation */}
                <div className="lg:col-span-5 space-y-6 text-left">
                  <div className="h-12 w-12 rounded-2xl bg-teal-500/10 border border-teal-500/20 flex items-center justify-center">
                    <ShieldCheck className="h-6 w-6 text-teal-600" />
                  </div>
                  <h3 className="text-2xl sm:text-3xl font-serif font-black text-slate-900">Order Fulfillment Agent</h3>
                  <p className="text-slate-600 leading-relaxed text-sm sm:text-base">
                    Decides — before an order text ever goes out to nurses — whether a regional urgency incentive is needed to ensure coverage, and automatically validates it against pre-approved contract budgets and compliance parameters. Every decision is fully audited and subject to strict service level agreement (SLA) caps.
                  </p>

                  <div className="space-y-4">
                    <h4 className="text-xs font-bold text-slate-400 uppercase tracking-widest">Key Capabilities</h4>
                    <ul className="space-y-3">
                      <li className="flex gap-3 items-start text-xs sm:text-sm text-slate-700">
                        <CheckCircle className="h-4 w-4 text-[#38dcae] shrink-0 mt-0.5" />
                        <span><strong>Dynamic Staffing Optimization:</strong> Evaluates real-time nurse availability, timing constraints, and regional history to determine the optimal support needed for successful visit acceptance.</span>
                      </li>
                      <li className="flex gap-3 items-start text-xs sm:text-sm text-slate-700">
                        <CheckCircle className="h-4 w-4 text-[#38dcae] shrink-0 mt-0.5" />
                        <span><strong>Contract Budget Protection:</strong> Automatically cross-references pre-approved client billing rates and standard regional guidelines to ensure every assignment remains compliant with service parameters.</span>
                      </li>
                      <li className="flex gap-3 items-start text-xs sm:text-sm text-slate-700">
                        <CheckCircle className="h-4 w-4 text-[#38dcae] shrink-0 mt-0.5" />
                        <span><strong>Intelligent Escalation:</strong> Automatically adjusts the coordination priority and notification frequency as deadlines near, ensuring critical care starts on time without exceeding budget caps.</span>
                      </li>
                      <li className="flex gap-3 items-start text-xs sm:text-sm text-slate-700">
                        <CheckCircle className="h-4 w-4 text-[#38dcae] shrink-0 mt-0.5" />
                        <span><strong>Compliance Logging:</strong> Fully logs decision parameters, input signals, and policy checks for every order. Items requiring manual exceptions are flagged instantly for operations review.</span>
                      </li>
                    </ul>
                  </div>
                </div>

                {/* Right Incentive Decision Simulator */}
                <div className="lg:col-span-7 bg-slate-950 rounded-2xl border border-slate-900 p-6 flex flex-col min-h-[520px] shadow-inner relative overflow-hidden">
                  <div className="flex items-center justify-between border-b border-slate-900 pb-3 mb-4 shrink-0">
                    <div className="flex items-center gap-2">
                      <Zap className="h-4 w-4 text-amber-400" />
                      <span className="text-xs font-bold uppercase tracking-wider text-slate-400">Atulo Incentive Decision Engine</span>
                    </div>
                    <span className="text-xs text-amber-400 font-semibold px-2 py-0.5 bg-amber-950/40 rounded border border-amber-500/20">AUTO INCENTIVES</span>
                  </div>

                  {incentiveStage === "idle" && (
                    <div className="flex-1 flex flex-col items-center justify-center text-center p-6">
                      <div className="h-16 w-16 rounded-2xl bg-slate-900 border border-slate-800 flex items-center justify-center mb-4 text-slate-400">
                        <ShieldCheck className="h-8 w-8" />
                      </div>
                      <h4 className="font-bold text-sm text-slate-300 mb-1">New Order Ready to Text Nurses</h4>
                      <p className="text-xs text-slate-500 max-w-sm mb-6">
                        Sample order loaded: <span className="text-amber-400 font-semibold">Remicade — 3.5 hr visit in 2 days, one eligible nurse within 30 miles</span>. Click to watch the agent gather market signals, determine the urgency level, then run the compliance guardrails before the text goes out.
                      </p>
                      <button
                        onClick={startIncentiveDemo}
                        className="bg-amber-600 hover:bg-amber-500 text-white font-bold text-xs sm:text-sm px-6 py-3 rounded-xl transition-all shadow-lg shadow-amber-500/10 active:scale-95"
                      >
                        Run Incentive Decision
                      </button>
                    </div>
                  )}

                  {incentiveStage === "deciding" && (
                    <div className="flex-1 flex flex-col items-center justify-center text-center gap-3">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500" />
                      <h4 className="font-bold text-xs text-slate-300 uppercase tracking-widest animate-pulse">Gathering Signals &amp; Verifying Compliance...</h4>
                      <p className="text-xs text-slate-500 max-w-xs leading-relaxed">
                        Counting eligible nurses, checking days to start, pulling center pickup history, and verifying compliance with partner contract parameters...
                      </p>
                    </div>
                  )}

                  {incentiveStage === "decided" && (
                    <div className="flex-1 overflow-y-auto space-y-4 pr-1 scrollbar-thin scrollbar-thumb-slate-800 animate-fade-in text-left">
                      <div className="flex justify-between items-center bg-emerald-950/30 border border-emerald-900/40 p-3 rounded-xl">
                        <div className="flex items-center gap-2">
                          <CheckCircle className="h-4 w-4 text-emerald-400" />
                          <span className="text-xs font-bold text-white uppercase tracking-wider">
                            Final: {INCENTIVE_DEMO.capped.approvedLevel}
                          </span>
                        </div>
                        <button
                          onClick={() => setIncentiveStage("idle")}
                          className="text-[10px] text-amber-400 hover:text-amber-300 font-semibold"
                        >
                          Reset Simulator
                        </button>
                      </div>

                      {/* Step 1: Operational signals */}
                      <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-4 space-y-3">
                        <div className="flex gap-2 items-center text-xs font-bold text-slate-400 border-b border-slate-800 pb-1.5 uppercase">
                          <Activity className="h-3.5 w-3.5 text-amber-400" />
                          Step 1 — Market Signals
                        </div>
                        <div className="grid grid-cols-2 gap-3 text-xs">
                          <div>
                            <span className="text-slate-500 block mb-0.5">Nurses within 30 mi</span>
                            <span className="text-white font-semibold">1</span>
                          </div>
                          <div>
                            <span className="text-slate-500 block mb-0.5">Days to first visit</span>
                            <span className="text-white font-semibold">2</span>
                          </div>
                          <div>
                            <span className="text-slate-500 block mb-0.5">Center pickup rate</span>
                            <span className="text-white font-semibold">42%</span>
                          </div>
                          <div>
                            <span className="text-slate-500 block mb-0.5">Visit duration</span>
                            <span className="text-white font-semibold">{INCENTIVE_DEMO.visitHours} hrs</span>
                          </div>
                        </div>
                        <p className="text-[11px] text-amber-300/90 pt-1 border-t border-slate-800">
                          Operational recommendation: <strong>{INCENTIVE_DEMO.operational.recommendation}</strong>
                        </p>
                      </div>

                      {/* Step 2: Contract & Policy Verification */}
                      <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-4 space-y-3">
                        <div className="flex gap-2 items-center text-xs font-bold text-slate-400 border-b border-slate-800 pb-1.5 uppercase">
                          <BarChart3 className="h-3.5 w-3.5 text-emerald-400" />
                          Step 2 — Contract &amp; Policy Verification
                        </div>

                        <div className="bg-slate-950/60 rounded-lg p-3 font-mono text-[10px] text-slate-300 space-y-1.5">
                          <p className="text-emerald-400 font-bold uppercase tracking-wider text-[9px] mb-1">A. Contract SLA Guidelines</p>
                          <p><span className="text-slate-500">Service Plan:</span> Springfield Region Standard SLA</p>
                          <p><span className="text-slate-500">Verification:</span> Cost-to-revenue ratio verification matches baseline parameters</p>
                          <p><span className="text-slate-500">Status:</span> Approved standard billing tier check</p>
                        </div>

                        <div className="bg-slate-950/60 rounded-lg p-3 font-mono text-[10px] text-slate-300 space-y-1.5">
                          <p className="text-rose-400 font-bold uppercase tracking-wider text-[9px] mb-1">B. Operational Budget Check</p>
                          <p><span className="text-slate-500">Rule base:</span> Region-specific scheduling constraints (1 nurse eligible)</p>
                          <p><span className="text-slate-500">SLA Cost Cap:</span> Active (prevents override from exceeding contract ceiling)</p>
                          <p><span className="text-slate-500">Status:</span> Limit rule matched for Level 2 recommendations</p>
                        </div>

                        <div className="grid grid-cols-2 gap-3 text-xs pt-1 border-t border-slate-800">
                          <div>
                            <span className="text-slate-500 block mb-0.5">SLA Plan Matching</span>
                            <span className="text-white font-semibold">ACTIVE</span>
                          </div>
                          <div>
                            <span className="text-slate-500 block mb-0.5">Cost Neutrality Check</span>
                            <span className="text-emerald-400 font-semibold font-mono">PASS</span>
                          </div>
                        </div>
                      </div>

                      {/* Step 3: SLA Budget Cap Applied */}
                      <div className="bg-amber-950/20 border border-amber-900/40 rounded-xl p-4 space-y-3">
                        <div className="flex gap-2 items-center text-xs font-bold text-amber-400 uppercase">
                          <ShieldCheck className="h-3.5 w-3.5" />
                          Step 3 — SLA Budget Cap Applied
                        </div>
                        <div className="flex items-center justify-between text-xs gap-2">
                          <span className="text-slate-400 line-through">Level 2 Recommended</span>
                          <ArrowRight className="h-3.5 w-3.5 text-amber-500 shrink-0" />
                          <span className="text-white font-bold">Level 0 Applied</span>
                        </div>
                        <p className="text-[11px] text-slate-400">
                          The regional urgency recommendation of Level 2 exceeded the pre-approved client agreement budget limit. The agent automatically scaled the staffing offer down to Level 0 to remain compliant.
                        </p>
                        {/* Margin bar visualization */}
                        <div className="space-y-1.5">
                          <div className="flex justify-between text-[10px] text-slate-500">
                            <span>SLA Compliance Index</span>
                            <span className="text-emerald-400 font-bold">100% (Compliant)</span>
                          </div>
                          <div className="h-2.5 bg-slate-800 rounded-full overflow-hidden relative">
                            <div
                              className="absolute inset-y-0 left-0 bg-emerald-500 rounded-full w-full"
                            />
                          </div>
                          <div className="flex justify-between text-[9px] text-slate-600">
                            <span>Non-compliant</span>
                            <span>Target Threshold</span>
                            <span>Fully Compliant</span>
                          </div>
                        </div>
                      </div>

                      {/* Odds comparison */}
                      <div className="grid grid-cols-2 gap-3">
                        <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-4 text-center">
                          <p className="text-[10px] text-slate-500 uppercase font-bold tracking-wider mb-1">Pickup odds without adjustment</p>
                          <p className="text-2xl font-black text-rose-400">{INCENTIVE_DEMO.pickupOddsBase}%</p>
                        </div>
                        <div className="bg-slate-900/60 border border-emerald-900/40 rounded-xl p-4 text-center">
                          <p className="text-[10px] text-slate-500 uppercase font-bold tracking-wider mb-1">With Level 0 adjustment</p>
                          <p className="text-2xl font-black text-emerald-400">{INCENTIVE_DEMO.pickupOddsOptimized}%</p>
                        </div>
                      </div>

                      {/* Reasoning quote */}
                      <div className="bg-slate-900/60 border border-slate-800 border-l-4 border-l-amber-500/60 rounded-xl p-4">
                        <p className="text-[10px] text-amber-400 uppercase font-bold tracking-wider mb-2">Agent Reasoning (Logged on the Order)</p>
                        <p className="text-xs text-slate-300 leading-relaxed italic">
                          &quot;Coverage is thin — one nurse within 30 miles — and the center&apos;s 42% pickup rate is weak with only 2 days to start. I recommended Level 2 support to maximize pickup odds. The contract safety guardrail automatically capped this to Level 0 to remain within pre-approved budget boundaries. The order is processed with Level 0 support and flagged for operations review due to the regional resource constraint.&quot;
                        </p>
                      </div>

                      <div className="flex items-start gap-2 bg-amber-950/30 border border-amber-700/40 rounded-lg p-3">
                        <AlertTriangle className="h-4 w-4 text-amber-400 shrink-0 mt-0.5" />
                        <p className="text-[11px] text-amber-200/90 leading-relaxed">
                          <strong>Resource flag raised:</strong> Springfield SLA constraint triggered. Order routed to ops for optional manual coordination, while automated staffing notifications proceed using the compliant Level 0 parameters.
                        </p>
                      </div>

                      <p className="text-[11px] text-slate-400">
                        The approved Level 0 adjustment is stamped on the visit, a full policy audit is logged on the order, and the compliant notification is sent to eligible regional nurses.
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Leadership Review Material */}
              <div className="mt-12 pt-10 border-t border-slate-200/80 space-y-12 text-left">
                <div className="max-w-3xl">
                  <span className="text-[10px] font-bold text-[#15b78e] uppercase tracking-widest bg-[#38dcae]/10 px-3 py-1 rounded-full border border-[#38dcae]/20 inline-block mb-4">
                    Leadership Review
                  </span>
                  <h3 className="text-2xl sm:text-3xl font-serif font-black text-slate-900 mb-3">How the Order Fulfillment Agent Works</h3>
                  <p className="text-slate-600 text-sm sm:text-base leading-relaxed">
                    A plain-language walkthrough of what the agent evaluates, how it sizes staffing adjustments, how client agreements are protected, and when orders get flagged — all before a single nurse notification goes out.
                  </p>
                </div>

                {/* How it decides: four inputs */}
                <div className="space-y-5">
                  <h4 className="text-xs font-bold text-slate-400 uppercase tracking-widest">How It Decides — Four Inputs, Then a Policy-Safe Answer</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="bg-slate-50 border border-slate-200/60 rounded-2xl p-5 space-y-2">
                      <div className="h-9 w-9 rounded-xl bg-teal-500/10 border border-teal-500/20 flex items-center justify-center">
                        <Users className="h-5 w-5 text-teal-600" />
                      </div>
                      <h5 className="font-bold text-slate-900 text-sm">Market Saturation</h5>
                      <p className="text-xs text-slate-600 leading-relaxed">How many eligible nurses are within 30 and 60 miles of the patient&apos;s zip code — and how far away the nearest one is.</p>
                    </div>
                    <div className="bg-slate-50 border border-slate-200/60 rounded-2xl p-5 space-y-2">
                      <div className="h-9 w-9 rounded-xl bg-teal-500/10 border border-teal-500/20 flex items-center justify-center">
                        <Clock className="h-5 w-5 text-teal-600" />
                      </div>
                      <h5 className="font-bold text-slate-900 text-sm">The Clock</h5>
                      <p className="text-xs text-slate-600 leading-relaxed">How many days remain until the first visit needs to happen. Tighter deadlines justify stronger offers.</p>
                    </div>
                    <div className="bg-slate-50 border border-slate-200/60 rounded-2xl p-5 space-y-2">
                      <div className="h-9 w-9 rounded-xl bg-teal-500/10 border border-teal-500/20 flex items-center justify-center">
                        <Activity className="h-5 w-5 text-teal-600" />
                      </div>
                      <h5 className="font-bold text-slate-900 text-sm">Center History</h5>
                      <p className="text-xs text-slate-600 leading-relaxed">The referral center&apos;s track record — how often its orders get picked up and how quickly nurses typically accept them.</p>
                    </div>
                    <div className="bg-slate-50 border border-slate-200/60 rounded-2xl p-5 space-y-2">
                      <div className="h-9 w-9 rounded-xl bg-teal-500/10 border border-teal-500/20 flex items-center justify-center">
                        <Percent className="h-5 w-5 text-teal-600" />
                      </div>
                      <h5 className="font-bold text-slate-900 text-sm">Contract Parameters</h5>
                      <p className="text-xs text-slate-600 leading-relaxed">Pre-approved regional budgets and service level agreement (SLA) constraints — used to set maximum bounds for incentives.</p>
                    </div>
                  </div>

                  <div className="flex flex-col lg:flex-row items-center gap-3">
                    <div className="flex-1 w-full bg-white border border-slate-200 rounded-xl p-4 text-center">
                      <p className="text-xs font-bold text-slate-900 mb-1">1. Gather signals</p>
                      <p className="text-[11px] text-slate-500 leading-relaxed">Market + urgency metadata — de-identified aggregates only.</p>
                    </div>
                    <ArrowRight className="h-5 w-5 text-teal-500 shrink-0 rotate-90 lg:rotate-0" />
                    <div className="flex-1 w-full bg-white border border-slate-200 rounded-xl p-4 text-center">
                      <p className="text-xs font-bold text-slate-900 mb-1">2. Size urgency support</p>
                      <p className="text-[11px] text-slate-500 leading-relaxed">AI determines recommended staffing tier based on lead time and density.</p>
                    </div>
                    <ArrowRight className="h-5 w-5 text-teal-500 shrink-0 rotate-90 lg:rotate-0" />
                    <div className="flex-1 w-full bg-amber-50 border border-amber-200 rounded-xl p-4 text-center">
                      <p className="text-xs font-bold text-amber-900 mb-1">3. Contract budget check</p>
                      <p className="text-[11px] text-amber-800/80 leading-relaxed">Clamp support recommendation to verify it fits within SLA rules.</p>
                    </div>
                    <ArrowRight className="h-5 w-5 text-teal-500 shrink-0 rotate-90 lg:rotate-0" />
                    <div className="flex-1 w-full bg-white border border-slate-200 rounded-xl p-4 text-center">
                      <p className="text-xs font-bold text-slate-900 mb-1">4. Confirm &amp; route</p>
                      <p className="text-[11px] text-slate-500 leading-relaxed">Stamp approved adjustment on visits; flag if manual review is triggered.</p>
                    </div>
                  </div>
                </div>

                {/* Operational Integrity & Sustainability Guardrails */}
                <div className="space-y-5">
                  <h4 className="text-xs font-bold text-slate-400 uppercase tracking-widest flex items-center gap-2">
                    <BarChart3 className="h-4 w-4 text-[#15b78e]" />
                    Operational Integrity &amp; Sustainability Guardrails
                  </h4>
                  <div className="bg-slate-50 border border-slate-200/60 rounded-2xl p-5 sm:p-6 space-y-5">
                    <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
                      Before any staffing support adjustment is confirmed, the agent runs compliance checks against the partner&apos;s commercial agreement. The goal is to staff hard-to-fill orders while ensuring full compliance with contract parameters.
                    </p>

                    {/* Data sources */}
                    <div className="overflow-hidden rounded-xl border border-slate-200">
                      <table className="w-full text-left text-xs">
                        <thead className="bg-slate-100">
                          <tr>
                            <th className="px-4 py-2.5 font-bold text-slate-500 uppercase tracking-wider text-[10px]">Field</th>
                            <th className="px-4 py-2.5 font-bold text-slate-500 uppercase tracking-wider text-[10px]">Database model</th>
                            <th className="px-4 py-2.5 font-bold text-slate-500 uppercase tracking-wider text-[10px]">Used for</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 bg-white text-slate-700">
                          <tr><td className="px-4 py-2">geographicCoverage</td><td className="px-4 py-2 font-mono text-[10px]">Nurse</td><td className="px-4 py-2">Staff locations relative to patient address</td></tr>
                          <tr><td className="px-4 py-2">daysToStart</td><td className="px-4 py-2 font-mono text-[10px]">Treatment / Order</td><td className="px-4 py-2">Lead time to schedule first visit (urgency factor)</td></tr>
                          <tr><td className="px-4 py-2">historicalAcceptance</td><td className="px-4 py-2 font-mono text-[10px]">ReferralCenter</td><td className="px-4 py-2">Partner-specific historical acceptance patterns</td></tr>
                          <tr><td className="px-4 py-2">slaContractCap</td><td className="px-4 py-2 font-mono text-[10px]">ReferralCenter</td><td className="px-4 py-2">Pre-approved regional budget parameters</td></tr>
                        </tbody>
                      </table>
                    </div>

                    {/* SVG Flowchart */}
                    <div className="bg-white border border-slate-200 rounded-xl p-4 sm:p-5 space-y-4">
                      <p className="font-bold text-slate-900 text-sm">Policy-Safe Decision Flow</p>
                      <svg viewBox="0 0 720 100" className="w-full h-auto" role="img" aria-label="Staffing compliance flow: market signals and timing constraints determine recommended support, which is verified against SLA contract budget limits before final approval.">
                        <rect x="10" y="30" width="140" height="40" rx="8" fill="#eff6ff" stroke="#3b82f6" />
                        <text x="80" y="55" fontSize="11" fontWeight="700" fill="#1d4ed8" textAnchor="middle">Market Signals</text>
                        <text x="165" y="55" fontSize="14" fontWeight="800" fill="#94a3b8">→</text>
                        <rect x="185" y="30" width="140" height="40" rx="8" fill="#fffbeb" stroke="#f59e0b" />
                        <text x="255" y="55" fontSize="11" fontWeight="700" fill="#b45309" textAnchor="middle">Urgency Support</text>
                        <text x="340" y="55" fontSize="14" fontWeight="800" fill="#94a3b8">→</text>
                        <rect x="360" y="30" width="160" height="40" rx="8" fill="#ecfdf5" stroke="#10b981" />
                        <text x="440" y="50" fontSize="11" fontWeight="700" fill="#047857" textAnchor="middle">SLA Budget Check</text>
                        <text x="440" y="64" fontSize="9" fill="#059669" textAnchor="middle">Contract Compliance</text>
                        <text x="535" y="55" fontSize="14" fontWeight="800" fill="#94a3b8">→</text>
                        <rect x="555" y="30" width="150" height="40" rx="8" fill="#f8fafc" stroke="#64748b" />
                        <text x="630" y="55" fontSize="11" fontWeight="700" fill="#475569" textAnchor="middle">Approved &amp; Routed</text>
                      </svg>
                    </div>

                    <div className="bg-white border border-emerald-200 rounded-xl p-4 space-y-3">
                      <p className="text-xs font-bold text-slate-900">Compliance Rules (applied in order)</p>
                      <ol className="space-y-2 text-xs text-slate-700 list-decimal list-inside">
                        <li><strong>Evaluate operational signals:</strong> Assess geographic density and deadline urgency.</li>
                        <li><strong>Apply SLA budget limits:</strong> Ensure urgency support recommendation does not exceed the partner&apos;s pre-approved service ceiling.</li>
                        <li><strong>Enforce cost-neutrality rules:</strong> Guarantee visit sustainability parameters are met before any notification sends.</li>
                        <li><strong>Snap to standard tiers:</strong> Align final adjustment to pre-authorized support levels (Levels 0–3).</li>
                      </ol>
                    </div>

                    <div className="overflow-hidden rounded-xl border border-slate-200">
                      <table className="w-full text-left text-xs sm:text-sm">
                        <thead className="bg-slate-100">
                          <tr>
                            <th className="px-4 py-3 font-bold text-slate-500 uppercase tracking-wider text-[10px]">Outcome</th>
                            <th className="px-4 py-3 font-bold text-slate-500 uppercase tracking-wider text-[10px]">What happens</th>
                            <th className="px-4 py-3 font-bold text-slate-500 uppercase tracking-wider text-[10px]">Visible to ops</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 bg-white">
                          <tr>
                            <td className="px-4 py-3 text-emerald-700 font-semibold">Fully Compliant</td>
                            <td className="px-4 py-3 text-slate-600">Standard support level approved; notifications sent immediately</td>
                            <td className="px-4 py-3 text-slate-500">Green active status on decision log</td>
                          </tr>
                          <tr>
                            <td className="px-4 py-3 text-amber-700 font-semibold">Exceeds Budget SLA Limit</td>
                            <td className="px-4 py-3 text-slate-600">Urgency level adjusted to compliant ceiling; notifications proceed</td>
                            <td className="px-4 py-3 text-slate-500"><span className="inline-flex items-center gap-1"><AlertTriangle className="h-3 w-3 text-amber-500" /> Compliance flag on order</span></td>
                          </tr>
                          <tr>
                            <td className="px-4 py-3 text-rose-700 font-semibold">Outlier Regional Resource Constraint</td>
                            <td className="px-4 py-3 text-slate-600">Incentive held at base level; order routed for manual operations override</td>
                            <td className="px-4 py-3 text-slate-500"><span className="inline-flex items-center gap-1"><ShieldAlert className="h-3 w-3 text-rose-500" /> Resource flag raised</span></td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                {/* Coverage ladder */}
                <div className="space-y-3">
                  <h4 className="text-xs font-bold text-slate-400 uppercase tracking-widest flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-[#15b78e]" />
                    The Coverage Ladder
                  </h4>
                  <div className="overflow-hidden rounded-2xl border border-slate-200/80">
                    <table className="w-full text-left text-xs sm:text-sm">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="px-4 py-3 font-bold text-slate-500 uppercase tracking-wider text-[10px] sm:text-xs">Nurse coverage near the patient</th>
                          <th className="px-4 py-3 font-bold text-slate-500 uppercase tracking-wider text-[10px] sm:text-xs text-right">Standard Urgency Tier</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 bg-white">
                        <tr>
                          <td className="px-4 py-3 text-slate-700">5 or more nurses within 30 miles</td>
                          <td className="px-4 py-3 text-right font-bold text-slate-400">Level 0 (Standard Base)</td>
                        </tr>
                        <tr>
                          <td className="px-4 py-3 text-slate-700">2–4 nurses within 30 miles</td>
                          <td className="px-4 py-3 text-right font-bold text-teal-600">Level 1 Urgency Support</td>
                        </tr>
                        <tr>
                          <td className="px-4 py-3 text-slate-700">1 nurse within 30 miles</td>
                          <td className="px-4 py-3 text-right font-bold text-teal-600">Level 2 Urgency Support</td>
                        </tr>
                        <tr>
                          <td className="px-4 py-3 text-slate-700">None within 30 miles, nearest within 60</td>
                          <td className="px-4 py-3 text-right font-bold text-amber-600">Level 3 Urgency Support</td>
                        </tr>
                        <tr>
                          <td className="px-4 py-3 text-slate-700">Nearest nurse beyond 60 miles</td>
                          <td className="px-4 py-3 text-right font-bold text-rose-600">Max Regional Override</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <p className="text-xs text-slate-500 leading-relaxed">
                    These levels represent standardized regional staffing adjustments, not hard-coded pricing promises. Under the new model, regional urgency settings align with partner SLA parameters to protect budgets.
                  </p>
                </div>

                {/* Escalation timeline */}
                <div className="space-y-3">
                  <h4 className="text-xs font-bold text-slate-400 uppercase tracking-widest flex items-center gap-2">
                    <Zap className="h-4 w-4 text-[#15b78e]" />
                    Deadline Escalation
                  </h4>
                  <div className="bg-slate-50 border border-slate-200/60 rounded-2xl p-5 sm:p-6">
                    <svg viewBox="0 0 720 125" className="w-full h-auto" role="img" aria-label="Escalation timeline: no urgency bump until 3 days before the visit start, adding Tier 1 support in the last 3 days, and Tier 2 support in the last 24 hours">
                      <text x="10" y="16" fontSize="11" fontWeight="700" fill="#94a3b8">ORDER CREATED</text>
                      <text x="710" y="16" fontSize="11" fontWeight="700" fill="#94a3b8" textAnchor="end">VISIT START</text>

                      <text x="396" y="34" fontSize="11" fontWeight="700" fill="#b45309" textAnchor="middle">3 days before start</text>
                      <text x="610" y="34" fontSize="11" fontWeight="700" fill="#be123c" textAnchor="middle">24 hours</text>
                      <line x1="396" y1="40" x2="396" y2="56" stroke="#f59e0b" strokeWidth="2" strokeDasharray="3 3" />
                      <line x1="610" y1="40" x2="610" y2="56" stroke="#f43f5e" strokeWidth="2" strokeDasharray="3 3" />

                      <rect x="10" y="56" width="386" height="34" rx="8" fill="#f1f5f9" stroke="#cbd5e1" />
                      <rect x="396" y="56" width="214" height="34" rx="8" fill="#fef3c7" stroke="#f59e0b" />
                      <rect x="610" y="56" width="100" height="34" rx="8" fill="#ffe4e6" stroke="#f43f5e" />

                      <text x="203" y="78" fontSize="13" fontWeight="800" fill="#475569" textAnchor="middle">No urgency bump</text>
                      <text x="503" y="78" fontSize="13" fontWeight="800" fill="#b45309" textAnchor="middle">+ Tier 1 Urgency Support</text>
                      <text x="660" y="78" fontSize="13" fontWeight="800" fill="#be123c" textAnchor="middle">+ Tier 2 Support</text>

                      <text x="203" y="112" fontSize="10.5" fill="#64748b" textAnchor="middle">Standard offer from the ladder</text>
                      <text x="503" y="112" fontSize="10.5" fill="#64748b" textAnchor="middle">Last 3 days before start</text>
                      <text x="660" y="112" fontSize="10.5" fill="#64748b" textAnchor="middle">Last 24 hours</text>
                    </svg>
                  </div>
                  <p className="text-xs text-slate-500 leading-relaxed">
                    When an order crosses into a higher urgency band, staffing priority is automatically increased — but every escalation remains subject to pre-approved partner agreement budget caps.
                  </p>
                </div>

                {/* Guardrails */}
                <div className="space-y-5">
                  <h4 className="text-xs font-bold text-slate-400 uppercase tracking-widest flex items-center gap-2">
                    <ShieldCheck className="h-4 w-4 text-[#15b78e]" />
                    Guardrails
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
                    <div className="flex gap-3 items-start text-xs sm:text-sm text-slate-700">
                      <CheckCircle className="h-4 w-4 text-emerald-500 shrink-0 mt-0.5" />
                      <span><strong>SLA Budget Caps:</strong> No automatic staffing adjustment may exceed the pre-approved budget ceilings set in partner agreements.</span>
                    </div>
                    <div className="flex gap-3 items-start text-xs sm:text-sm text-slate-700">
                      <CheckCircle className="h-4 w-4 text-emerald-500 shrink-0 mt-0.5" />
                      <span><strong>Cost Neutrality:</strong> Automated optimizations are continuously balanced against target service economics.</span>
                    </div>
                    <div className="flex gap-3 items-start text-xs sm:text-sm text-slate-700">
                      <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
                      <span><strong>Compliance Flags:</strong> When resource constraints require adjustments outside standard budgets, the system flags the order for operations override.</span>
                    </div>
                    <div className="flex gap-3 items-start text-xs sm:text-sm text-slate-700">
                      <CheckCircle className="h-4 w-4 text-[#38dcae] shrink-0 mt-0.5" />
                      <span><strong>Off by Default:</strong> The agent is disabled until explicitly enabled for a specific partner or region.</span>
                    </div>
                    <div className="flex gap-3 items-start text-xs sm:text-sm text-slate-700">
                      <CheckCircle className="h-4 w-4 text-[#38dcae] shrink-0 mt-0.5" />
                      <span><strong>Three-Level Switch:</strong> Enable or disable the agent globally, per referral center, or for individual orders.</span>
                    </div>
                    <div className="flex gap-3 items-start text-xs sm:text-sm text-slate-700">
                      <CheckCircle className="h-4 w-4 text-[#38dcae] shrink-0 mt-0.5" />
                      <span><strong>Bounded Levels:</strong> AI recommendations are strictly bound to predefined support levels (Levels 0–3) to prevent outliers.</span>
                    </div>
                    <div className="flex gap-3 items-start text-xs sm:text-sm text-slate-700">
                      <CheckCircle className="h-4 w-4 text-[#38dcae] shrink-0 mt-0.5" />
                      <span><strong>Integrations Untouched:</strong> System output integrates cleanly with existing admin scheduling workflows, ensuring process continuity.</span>
                    </div>
                    <div className="flex gap-3 items-start text-xs sm:text-sm text-slate-700">
                      <CheckCircle className="h-4 w-4 text-[#38dcae] shrink-0 mt-0.5" />
                      <span><strong>Humans Always Win:</strong> Operations admins retain ultimate control and can manually adjust or override any automated recommendation.</span>
                    </div>
                    <div className="flex gap-3 items-start text-xs sm:text-sm text-slate-700">
                      <CheckCircle className="h-4 w-4 text-[#38dcae] shrink-0 mt-0.5" />
                      <span><strong>Offers Never Shrink:</strong> Once a regional adjustment is sent to staff, the support level remains stable and does not decrease.</span>
                    </div>
                    <div className="flex gap-3 items-start text-xs sm:text-sm text-slate-700">
                      <CheckCircle className="h-4 w-4 text-[#38dcae] shrink-0 mt-0.5" />
                      <span><strong>Privacy by Design:</strong> Only de-identified operational parameters are processed — no patient names or full addresses are sent to the model.</span>
                    </div>
                    <div className="flex gap-3 items-start text-xs sm:text-sm text-slate-700">
                      <CheckCircle className="h-4 w-4 text-[#38dcae] shrink-0 mt-0.5" />
                      <span><strong>Every Decision Logged:</strong> Input signals and verification outcomes are recorded transparently on the order log.</span>
                    </div>
                  </div>
                </div>

                {/* What nurses see */}
                <div className="space-y-3">
                  <h4 className="text-xs font-bold text-slate-400 uppercase tracking-widest flex items-center gap-2">
                    <MessageSquare className="h-4 w-4 text-[#15b78e]" />
                    What Nurses See
                  </h4>
                  <div className="bg-slate-950 rounded-2xl border border-slate-900 p-6">
                    <p className="text-xs text-slate-400 mb-3 font-semibold">
                      Sample SMS — automated notification with region-adjusted staffing support:
                    </p>
                    <div className="flex justify-start">
                      <div className="max-w-[440px] bg-slate-900 border border-slate-800 text-slate-300 rounded-2xl rounded-bl-none px-4 py-3 text-sm leading-relaxed shadow-md">
                        New Atulo Health Referral for Remicade in Springfield, IL 62704!{" "}
                        <span className="bg-slate-800 text-slate-300 font-semibold rounded px-1">Approx. 3.5-hour visit.</span>{" "}
                        Open the Atulo app to view and accept.
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Coming Soon: Nurse Field Agent Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 pb-24 relative z-10">
        <div className="bg-white border border-slate-200/80 rounded-3xl p-8 sm:p-12 shadow-xl relative overflow-hidden text-left">
          {/* Ambient Glows */}
          <div className="absolute top-[-20%] left-[-20%] w-[400px] h-[400px] rounded-full bg-teal-500/5 blur-[100px] pointer-events-none" />
          <div className="absolute bottom-[-20%] right-[-20%] w-[400px] h-[400px] rounded-full bg-indigo-500/5 blur-[100px] pointer-events-none" />
          
          <div className="max-w-3xl space-y-6 relative z-10">
            <span className="text-[10px] font-bold text-[#15b78e] uppercase tracking-widest bg-[#38dcae]/10 px-3 py-1 rounded-full border border-[#38dcae]/20 inline-block">
              Coming Soon
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-black text-slate-900 leading-tight">
              6. The Nurse Field Agent
            </h2>
            <p className="text-slate-600 text-sm sm:text-base leading-relaxed">
              We are expanding the Atulo Intelligence Suite with our new autonomous Field Agent. 
              Designed for nurses on-site, it simplifies clinical issue routing by automatically analyzing, categorizing, and assigning problems to the right support teams in real time.
            </p>

            <div className="bg-slate-50 border border-slate-200/60 rounded-2xl p-6 space-y-4">
              <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider">How it works:</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-xs text-slate-600">
                <div className="space-y-2">
                  <div className="h-6 w-6 rounded-full bg-[#38dcae] text-white flex items-center justify-center font-bold text-[11px] shadow-sm shadow-teal-500/20">1</div>
                  <h5 className="font-bold text-slate-900">Flag & Add Notes</h5>
                  <p className="leading-relaxed">A nurse flags a visit directly inside the mobile app and adds brief notes detailing the issue.</p>
                </div>
                <div className="space-y-2">
                  <div className="h-6 w-6 rounded-full bg-[#38dcae] text-white flex items-center justify-center font-bold text-[11px] shadow-sm shadow-teal-500/20">2</div>
                  <h5 className="font-bold text-slate-900">Contextual Screenshot</h5>
                  <p className="leading-relaxed">The app automatically captures a screenshot of the active screen context to attach critical diagnostic details.</p>
                </div>
                <div className="space-y-2">
                  <div className="h-6 w-6 rounded-full bg-[#38dcae] text-white flex items-center justify-center font-bold text-[11px] shadow-sm shadow-teal-500/20">3</div>
                  <h5 className="font-bold text-slate-900">Atulo Intelligence Magic</h5>
                  <p className="leading-relaxed">Our Field Agent analyzes the text and pixels, determines priority, buckets it into categories, and routes it to the correct admin to resolve.</p>
                </div>
              </div>
            </div>

            <div className="pt-2 flex flex-wrap gap-4 items-center text-xs text-slate-500">
              <span className="flex items-center gap-1.5 text-[#15b78e] font-semibold">
                <span className="h-2 w-2 rounded-full bg-[#38dcae] animate-pulse inline-block" />
                Active Development
              </span>
              <span>•</span>
              <span>Target Release: Q3 2026</span>
            </div>
          </div>
        </div>
      </section>


      {/* Footer */}
      <footer className="w-full max-w-7xl mx-auto px-4 sm:px-8 py-12 flex flex-col md:flex-row items-center justify-between gap-6 border-t border-slate-200/60 text-xs text-slate-500">
        <div>
          &copy; {new Date().getFullYear()} Atulo Care LLC. All rights reserved.
        </div>
        <div className="flex gap-6">
          <a href="https://www.atulocare.com/privacy" target="_blank" rel="noreferrer" className="hover:text-teal-600 transition-colors">
            Privacy Policy
          </a>
          <a href="https://portalv2.atulocare.com/guide" target="_blank" rel="noreferrer" className="hover:text-teal-600 transition-colors">
            App Guide Document
          </a>
        </div>
      </footer>
    </div>
  );
}

// Fallback Help icon component
function HelpCircleIcon(props) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <circle cx="12" cy="12" r="10" />
      <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}
